/**
 * Handle inline image paste / drop.
 *
 *   - Intercepts paste/drop events that contain at least one image File.
 *   - Inserts a placeholder image node (data-loading="true") immediately so
 *     the user sees an upload affordance.
 *   - Calls the provided `uploader(file)` -> Promise<string|null> which is
 *     expected to attach the file via the existing
 *     `attachResourceToLessonNote` mutation and resolve with the final URL.
 *     On success, the placeholder src is replaced with the real URL and the
 *     loading flag cleared.
 *     On failure (or `null`), the placeholder node is removed and the caller
 *     is notified via `onError`.
 *   - If `getLessonNoteId()` returns null/empty, the handler fires `onMissingId()`
 *     (typically a toast: "Save the note first…") and returns without inserting.
 *
 * Implementation note: we tag placeholder nodes with a one-shot
 * `uploadId` attribute (stored in the image's `title`) so we can find them
 * again post-upload regardless of doc edits in flight. We re-scan the doc
 * each resolution rather than memoise a position — much simpler and robust.
 */
import { Plugin, PluginKey, TextSelection } from 'prosemirror-state';
import type { EditorView } from 'prosemirror-view';
import { pmSchema } from './pmSchema';

export const imageUploadPluginKey = new PluginKey('pmImageUpload');

export interface ImageUploadHandlers {
  getLessonNoteId: () => string | number | null | undefined
  upload: (file: File) => Promise<string | null>
  onMissingId: () => void
  onError: (msg: string) => void
}

function uid(): string {
  return `upl-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function pickImageFiles(items: DataTransferItemList | FileList | null): File[] {
  if (!items) return [];
  const out: File[] = [];
  if (items instanceof FileList) {
    Array.from(items).forEach((f) => { if (f.type.startsWith('image/')) out.push(f); });
    return out;
  }
  Array.from(items).forEach((it) => {
    if (it.kind === 'file') {
      const f = it.getAsFile();
      if (f && f.type.startsWith('image/')) out.push(f);
    }
  });
  return out;
}

function findPlaceholder(view: EditorView, uploadId: string): number | null {
  let pos: number | null = null;
  view.state.doc.descendants((node, p) => {
    if (pos !== null) return false;
    if (node.type === pmSchema.nodes.image && node.attrs.title === uploadId) {
      pos = p;
      return false;
    }
    return true;
  });
  return pos;
}

function insertPlaceholder(view: EditorView, file: File, uploadId: string) {
  const url = URL.createObjectURL(file);
  const node = pmSchema.nodes.image.create({
    src: url,
    alt: file.name,
    title: uploadId,
    dataLoading: 'true',
  });
  const tr = view.state.tr.replaceSelectionWith(node);
  view.dispatch(tr);
}

async function handleFiles(view: EditorView, files: File[], handlers: ImageUploadHandlers) {
  const noteId = handlers.getLessonNoteId();
  if (noteId === null || noteId === undefined || noteId === '') {
    handlers.onMissingId();
    return;
  }

  for (const file of files) {
    const uploadId = uid();
    insertPlaceholder(view, file, uploadId);
    handlers.upload(file)
      .then((finalUrl) => {
        const pos = findPlaceholder(view, uploadId);
        if (pos == null) return;
        if (!finalUrl) {
          // remove placeholder
          const tr = view.state.tr.delete(pos, pos + view.state.doc.nodeAt(pos)!.nodeSize);
          view.dispatch(tr);
          handlers.onError('Upload failed');
          return;
        }
        const node = view.state.doc.nodeAt(pos);
        if (!node) return;
        const newNode = pmSchema.nodes.image.create({
          src: finalUrl,
          alt: node.attrs.alt,
          title: null,
          dataLoading: null,
        });
        const tr = view.state.tr.replaceWith(pos, pos + node.nodeSize, newNode);
        view.dispatch(tr);
      })
      .catch((err) => {
        const pos = findPlaceholder(view, uploadId);
        if (pos != null) {
          const node = view.state.doc.nodeAt(pos);
          if (node) {
            const tr = view.state.tr.delete(pos, pos + node.nodeSize);
            view.dispatch(tr);
          }
        }
        const msg = err instanceof Error ? err.message : 'Upload failed';
        handlers.onError(msg);
      });
  }
}

export function buildImageUploadPlugin(handlers: ImageUploadHandlers) {
  return new Plugin({
    key: imageUploadPluginKey,
    props: {
      handlePaste(view, event) {
        const files = pickImageFiles(event.clipboardData?.items ?? null);
        if (files.length === 0) return false;
        event.preventDefault();
        handleFiles(view, files, handlers);
        return true;
      },
      handleDrop(view, event) {
        const files = pickImageFiles(event.dataTransfer?.files ?? null);
        if (files.length === 0) return false;
        event.preventDefault();
        // Move the selection to the drop point so the placeholder lands there.
        const coords = { left: event.clientX, top: event.clientY };
        const dropPos = view.posAtCoords(coords);
        if (dropPos) {
          const $pos = view.state.doc.resolve(dropPos.pos);
          const tr = view.state.tr.setSelection(TextSelection.near($pos));
          view.dispatch(tr);
        }
        handleFiles(view, files, handlers);
        return true;
      },
    },
  });
}
